import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Input } from 'antd';
import { cn, sanitizeTextInput } from '../../utils';
import { CustomRegisterOptions } from '../../types';
import { useEffect, useRef } from 'react';

const { TextArea } = Input;

interface TextInputProps<T extends FieldValues> {
  name: Path<T>;
  control?: UseFormReturn<T>['control'];
  form?: UseFormReturn<T>;
  rules?: CustomRegisterOptions<T>;
  label?: string;
  labelClassName?: string; // NEW
  formState?: UseFormReturn<T>['formState'];
  addOnClass?: string;
  config?: Record<string, any>;
  onPlaceSelected?: (place: { name: string; lat: number; lng: number }) => void;
  variant?: 'textarea' | 'text' | 'password';
}

const TextInput = <T extends FieldValues>({
  name,
  control,
  form,
  rules,
  label,
  labelClassName = '', // NEW
  formState,
  addOnClass = '',
  config = {},
  variant = 'text',
  onPlaceSelected,
}: TextInputProps<T>) => {
  const effectiveControl = control || form?.control;
  const effectiveFormState = formState || form?.formState;
  const errors = (effectiveFormState?.errors || {}) as Record<string, any>;

  if (!name || !effectiveControl) {
    return null;
  }

  const isGoogleAutocomplete = config.useGooglePlacesAutocomplete;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onChangeRef = useRef<((val: any) => void) | null>(null);

  useEffect(() => {
    if (!isGoogleAutocomplete || !inputRef.current || !window.google) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        componentRestrictions: { country: 'IN' },
      },
    );

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current?.getPlace();
      if (!place?.geometry?.location) return;

      const placeName = place.formatted_address || place.name || '';
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      onChangeRef.current?.(placeName);
      onPlaceSelected?.({ name: placeName, lat, lng });
    });
  }, [isGoogleAutocomplete]);

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label
          className={cn(
            'text-xs font-medium text-text-100 whitespace-nowrap',
            labelClassName, // Add custom label class here
          )}
        >
          {label}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          onChangeRef.current = field.onChange;

          // Create sanitized onChange handler for text and textarea
          const handleChange = (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            const sanitizedValue = sanitizeTextInput(e.target.value);
            field.onChange(sanitizedValue);
          };

          if (variant === 'textarea') {
            return (
              <TextArea
                className={addOnClass}
                {...field}
                {...config}
                onChange={handleChange}
              />
            );
          }

          if (variant === 'password') {
            return (
              <Input.Password
                className={addOnClass}
                {...field}
                {...config}
                onChange={handleChange}
              />
            );
          }

          return (
            <Input
              className={addOnClass}
              {...field}
              {...config}
              onChange={handleChange}
              ref={el => {
                const inputEl = el?.input;
                field.ref(inputEl);
                if (isGoogleAutocomplete && inputEl) {
                  inputRef.current = inputEl;
                }
              }}
            />
          );
        }}
      />

      <div
        className={cn(
          'text-xs text-red-500',
          { visible: errors[name] },
          { invisible: !errors[name] },
        )}
      >
        {errors[name] && `${errors[name]?.message}`}
      </div>
    </div>
  );
};

export default TextInput;

// import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
// import { Input } from 'antd';
// import { cn } from '../../utils';
// import { CustomRegisterOptions } from '../../types';
// import { useEffect, useRef } from 'react';

// const { TextArea } = Input;

// interface TextInputProps<T extends FieldValues> {
//   name: Path<T>;
//   control: UseFormReturn<T>['control'];
//   rules?: CustomRegisterOptions<T>;
//   label?: string;
//   formState: UseFormReturn<T>['formState'];
//   addOnClass?: string;
//   config?: Record<string, any>;
//   onPlaceSelected?: (place: { name: string; lat: number; lng: number }) => void;
//   variant?: 'textarea' | 'text' | 'password';
// }

// const TextInput = <T extends FieldValues>({
//   name,
//   control,
//   rules,
//   label,
//   formState,
//   addOnClass = '',
//   config = {},
//   variant = 'text',
//   onPlaceSelected,
// }: TextInputProps<T>) => {
//   const { errors } = formState as {
//     errors: Record<string, any>;
//   };

//   const isGoogleAutocomplete = config.useGooglePlacesAutocomplete;
//   const inputRef = useRef<HTMLInputElement | null>(null);
//   const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
//   const onChangeRef = useRef<((val: any) => void) | null>(null);

//   useEffect(() => {
//     if (!isGoogleAutocomplete || !inputRef.current || !window.google) return;

//     // Initialize the Autocomplete with country restriction
//     autocompleteRef.current = new window.google.maps.places.Autocomplete(
//       inputRef.current,
//       {
//         componentRestrictions: { country: 'IN' }, // Restrict to India
//       },
//     );

//     autocompleteRef.current.addListener('place_changed', () => {
//       const place = autocompleteRef.current?.getPlace();
//       if (!place || !place.geometry || !place.geometry.location) return;

//       const placeName = place.formatted_address || place.name || '';
//       const lat = place.geometry.location.lat();
//       const lng = place.geometry.location.lng();

//       onChangeRef.current?.(placeName); // update form field
//       onPlaceSelected?.({ name: placeName, lat, lng });
//     });
//   }, [isGoogleAutocomplete]);

//   return (
//     <div className="flex flex-col gap-1">
//       {label && <label className="text-xs font-medium text-text-100 whitespace-nowrap">{label}</label>}
//       <Controller
//         name={name}
//         control={control}
//         rules={rules}
//         render={({ field }) => {
//           onChangeRef.current = field.onChange;

//           if (variant === 'textarea') {
//             return <TextArea className={addOnClass} {...field} {...config} />;
//           }

//           if (variant === 'password') {
//             return (
//               <Input.Password className={addOnClass} {...field} {...config} />
//             );
//           }

//           return (
//             <Input
//               className={addOnClass}
//               {...field}
//               {...config}
//               ref={el => {
//                 const inputEl = el?.input as HTMLInputElement | null;
//                 field.ref(inputEl);
//                 if (isGoogleAutocomplete && inputEl) {
//                   inputRef.current = inputEl;
//                 }
//               }}
//             />
//           );
//         }}
//       />
//       <div
//         className={cn(
//           'text-xs text-red-500',
//           { visible: errors[name] },
//           { invisible: !errors[name] },
//         )}
//       >
//         {errors[name] && `${errors[name]?.message}`}
//       </div>
//     </div>
//   );
// };

// export default TextInput;
